import { useRef, useState } from 'react'
import { getLocalizedPath } from '../i18n/routes'
import { useI18n } from '../i18n/I18nProvider'
import { BrandSignature } from '../layout/Brand'
import { Button } from '../ui/Button'
import { Card, CardBody, CardStatus } from '../ui/Card'
import { Dialog } from '../ui/Dialog'
import { Input } from '../ui/Input'
import { Link } from '../ui/Link'
import { Sheet } from '../ui/Sheet'

export function ComponentSandbox() {
  const { language, t } = useI18n()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [pressed, setPressed] = useState(false)
  const dialogTriggerRef = useRef<HTMLButtonElement>(null)
  const sheetTriggerRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <section className="foundation-hero">
        <div className="page-frame foundation-hero__grid">
          <div className="foundation-hero__copy">
            <p className="section-kicker">{t('sandbox.kicker')}</p>
            <h1 tabIndex={-1}>{t('sandbox.title')}</h1>
            <p className="foundation-hero__intro">{t('sandbox.intro')}</p>
            <div className="foundation-hero__actions">
              <Link variant="primary" to={getLocalizedPath('vehicles', language)}>
                {t('sandbox.primaryAction')}
              </Link>
              <Link variant="secondary" to={getLocalizedPath('contacts', language)}>
                {t('sandbox.secondaryAction')}
              </Link>
            </div>
          </div>
          <div className="foundation-hero__passport" aria-hidden="true">
            <span className="passport-card__index">TS / 26</span>
            <span className="passport-card__word">AUTO</span>
            <span className="passport-card__rule" />
            <span className="passport-card__meta">4 · 6 · 12</span>
          </div>
          <div className="foundation-hero__signature">
            <BrandSignature />
          </div>
        </div>
      </section>

      <section className="specimen-section specimen-section--metal" aria-labelledby="type-heading">
        <div className="page-frame type-specimen">
          <div>
            <p className="section-kicker">{t('sandbox.typeLabel')}</p>
            <h2 id="type-heading">{t('sandbox.typeTitle')}</h2>
          </div>
          <p>{t('sandbox.typeBody')}</p>
          <dl className="data-strip">
            <div><dt>{t('sandbox.gridToken')}</dt><dd>12 / 6 / 4</dd></div>
            <div><dt>{t('sandbox.rhythmToken')}</dt><dd>4 / 8 px</dd></div>
            <div><dt>{t('sandbox.readingToken')}</dt><dd>68–72 ch</dd></div>
          </dl>
        </div>
      </section>

      <section className="specimen-section" aria-labelledby="controls-heading">
        <div className="page-frame">
          <div className="section-heading">
            <p className="section-kicker">{t('sandbox.controlsLabel')}</p>
            <h2 id="controls-heading">{t('sandbox.controlsTitle')}</h2>
          </div>

          <div className="control-specimen">
            <div className="control-specimen__buttons">
              <Button>{t('sandbox.buttonDefault')}</Button>
              <Button variant="secondary">{t('sandbox.buttonSecondary')}</Button>
              <Button
                variant="quiet"
                aria-pressed={pressed}
                onClick={() => setPressed((current) => !current)}
              >
                {t('sandbox.buttonSecondary')}
              </Button>
              <Button loading>{t('sandbox.buttonLoading')}</Button>
              <Button disabled>{t('sandbox.buttonDisabled')}</Button>
            </div>

            <div className="control-specimen__links">
              <Link variant="text" to={getLocalizedPath('contacts', language)}>{t('nav.contacts')}</Link>
              <Link variant="text" disabled to={getLocalizedPath('services', language)}>{t('sandbox.linkDisabled')}</Link>
            </div>

            <div className="control-specimen__fields">
              <Input label={t('sandbox.nameLabel')} hint={t('sandbox.nameHint')} autoComplete="name" />
              <Input
                label={t('sandbox.emailLabel')}
                error={t('sandbox.emailError')}
                type="email"
                defaultValue="nome@"
                autoComplete="email"
              />
              <Input
                label={t('sandbox.disabledLabel')}
                defaultValue={t('sandbox.disabledValue')}
                disabled
              />
            </div>

            <div className="control-specimen__overlays">
              <Button ref={dialogTriggerRef} variant="secondary" onClick={() => setDialogOpen(true)}>
                {t('sandbox.dialogAction')}
              </Button>
              <Button ref={sheetTriggerRef} variant="secondary" onClick={() => setSheetOpen(true)}>
                {t('sandbox.sheetAction')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="specimen-section specimen-section--dark" aria-labelledby="states-heading">
        <div className="page-frame">
          <div className="section-heading">
            <p className="section-kicker">{t('sandbox.statesLabel')}</p>
            <h2 id="states-heading">{t('sandbox.statesTitle')}</h2>
          </div>
          <div className="state-grid">
            <Card>
              <CardStatus label={t('sandbox.statusReady')} tone="accent" />
              <CardBody title={t('sandbox.cardReadyTitle')}><p>{t('sandbox.cardReadyBody')}</p></CardBody>
            </Card>
            <Card>
              <CardStatus label={t('sandbox.statusEmpty')} />
              <CardBody title={t('sandbox.cardEmptyTitle')}><p>{t('sandbox.cardEmptyBody')}</p></CardBody>
            </Card>
            <Card>
              <CardStatus label={t('sandbox.statusError')} tone="warning" />
              <CardBody title={t('sandbox.cardErrorTitle')}><p>{t('sandbox.cardErrorBody')}</p></CardBody>
            </Card>
          </div>
        </div>
      </section>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={t('sandbox.dialogTitle')}
        description={t('sandbox.dialogDescription')}
        closeLabel={t('common.close')}
        triggerRef={dialogTriggerRef}
      >
        <Button onClick={() => setDialogOpen(false)}>{t('sandbox.dialogConfirm')}</Button>
      </Dialog>

      <Sheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title={t('sandbox.sheetTitle')}
        description={t('sandbox.sheetDescription')}
        closeLabel={t('common.close')}
        triggerRef={sheetTriggerRef}
      >
        <Input label={t('sandbox.nameLabel')} hint={t('sandbox.nameHint')} />
        <Button onClick={() => setSheetOpen(false)}>{t('sandbox.dialogConfirm')}</Button>
      </Sheet>
    </>
  )
}
